import { TPublishOptions, TMutedList, TFeedSubRequest } from '@/types'
import FlexSearch from 'flexsearch'
import { EventTemplate, NostrEvent, VerifiedEvent } from '@nostr/tools/wasm'
import { SubCloser } from '@nostr/tools/abstract-pool'
import { binarySearch, mergeReverseSortedLists } from '@nostr/tools/utils'
import { seenOn } from '@nostr/gadgets/redstore'
import { RedEventStore } from '@nostr/gadgets/redstore'
import { outboxFilterRelayBatch } from '@nostr/gadgets/outbox'
import { Filter, matchFilters } from '@nostr/tools/filter'
import * as kinds from '@nostr/tools/kinds'
import { pool } from '@nostr/gadgets/global'
import { NostrUser, nostrUserFromEvent } from '@nostr/gadgets/metadata'
import { loadRelayList, loadMuteList } from '@nostr/gadgets/lists'
import { isHex32 } from '@nostr/gadgets/utils'
import i18n from 'i18next'
import { isValidPubkey } from '@/lib/pubkey'
import { tagNameEquals } from '@/lib/tag'
import { isLocalNetworkUrl, normalizeUrl } from '@/lib/url'
import { current, outbox, ready } from './outbox.service'
import { debounce, now } from '@/lib/utils'
import { store } from './store.service'
import { decode } from '@nostr/tools/nip19'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { getReplaceableCoordinateFromEvent, isReplaceableEvent } from '@/lib/event'
import { toast } from 'sonner'
import { getDefaultStore } from 'jotai'
import { currentPubkeyAtom, signerAtom } from '@/atoms/session'
import { unmarkPendingEvent } from '@/atoms/pending'
import { relayListAtom } from '@/atoms/lists'
import { feedKinds } from '@/universe'
import { getErrorMessage } from '@/lib/error'
import { authRelay, signAuthEvent } from './relay-auth.service'

const t = i18n.t.bind(i18n)
const jotaiStore = getDefaultStore()

const userIndex = new FlexSearch.Index({
  tokenize: 'full'
})

const newEventListeners = new Set<(event: NostrEvent) => void>()
const eventRemovedListeners = new Set<(eventId: string) => void>()

export function onNewEvent(listener: (event: NostrEvent) => void) {
  newEventListeners.add(listener)
  return () => {
    newEventListeners.delete(listener)
  }
}

export function onEventRemoved(listener: (eventId: string) => void) {
  eventRemovedListeners.add(listener)
  return () => {
    eventRemovedListeners.delete(listener)
  }
}

export type PublishResult = {
  relayUrl: string
  success: boolean
  reason: string
}

export function displayPublishResult(result: PublishResult) {
  if (result.success) {
    toast.success(t('Published to {{relay}}', { relay: result.relayUrl }))
  } else {
    toast.error(
      t('Failed to publish to {{relay}}: {{error}}', {
        relay: result.relayUrl,
        error: result.reason
      }),
      { duration: 10_000 }
    )
  }
}

async function init() {
  setupEventDeletionTracking()
  scheduleNextCleanup()

  try {
    for (const event of await store.queryEvents({ kinds: [0] })) {
      addUsernameToIndex(nostrUserFromEvent(event))
    }
  } catch (err) {
    console.warn('no profiles to index?', err)
  }

  pool.automaticallyAuth = () => async (authEvt: EventTemplate) => {
    const signer = await jotaiStore.get(signerAtom)

    if (signer) {
      return await signAuthEvent(authEvt, signer)
    }

    throw new Error("<not logged in, can't automatically auth>")
  }
}

export async function determineTargetRelays(
  event: NostrEvent,
  {
    specifiedRelayUrls,
    additionalRelayUrls,
    includeOurWriteRelays = true,
    includeTheirReadRelays = true,
    additionalMentionPubkeys
  }: TPublishOptions = {}
) {
  if (event.kind === kinds.Report) {
    const targetEventId = event.tags.find(tagNameEquals('e'))?.[1]
    const seenOnUrls = targetEventId ? getSeenEventRelayUrls(targetEventId) : []
    if (seenOnUrls.length) {
      return seenOnUrls
    }
  }

  let relays: string[]
  if (specifiedRelayUrls?.length) {
    relays = specifiedRelayUrls
  } else {
    const _additionalRelayUrls: string[] = additionalRelayUrls ?? []
    if (includeTheirReadRelays && event.kind !== kinds.Contacts && event.kind !== kinds.Mutelist) {
      const mentions: string[] = [...(additionalMentionPubkeys ?? [])]
      for (const [tagName, tagValue] of event.tags) {
        if (
          ['p', 'P'].includes(tagName) &&
          !!tagValue &&
          isValidPubkey(tagValue) &&
          !mentions.includes(tagValue)
        ) {
          mentions.push(tagValue)
        }
      }
      if (mentions.length > 0) {
        const relayLists = await Promise.all(mentions.map((pk) => loadRelayList(pk)))
        for (const { items } of relayLists) {
          _additionalRelayUrls.push(
            ...items
              .filter((r) => r.read)
              .map((r) => r.url)
              .slice(0, 3)
          )
        }
      }
    }
    if (
      event.kind === kinds.RelayList ||
      event.kind === kinds.Contacts ||
      event.kind === kinds.FavoriteRelays ||
      event.kind === kinds.BlossomServerList
    ) {
      _additionalRelayUrls.push(...window.hallway.universe.bigRelayUrls)
    }

    const ourWriteRelayUrls = includeOurWriteRelays
      ? (await getDefaultStore().get(relayListAtom))
          .filter((r) => r.write)
          .map((r) => r.url)
          .slice(0, 10)
      : []
    relays = ourWriteRelayUrls.concat(Array.from(new Set(_additionalRelayUrls)) ?? [])
  }

  if (!relays.length) {
    relays.push(...window.hallway.universe.bigRelayUrls)
  }

  return relays
}

export async function publish(
  evtt: EventTemplate,
  options: TPublishOptions = {}
): Promise<VerifiedEvent | null> {
  const signer = await jotaiStore.get(signerAtom)

  const evt = await signer?.signEvent(evtt)
  if (!evt) return null

  const relays = await determineTargetRelays(evt, options)
  for await (const result of publishEvent(relays, evt)) {
    displayPublishResult(result)
  }

  await addEventToCache(evt, isReplaceableEvent(evt.kind) ? evt.created_at : undefined)
  emitNewEvent(evt)

  return evt
}

export async function publishOpaquely(
  evtt: EventTemplate,
  options: TPublishOptions = {}
): Promise<VerifiedEvent | null> {
  const signer = await jotaiStore.get(signerAtom)

  const evt = await signer?.signEvent(evtt)
  if (!evt) return null

  const relays = await determineTargetRelays(evt, options)
  await publishEventOpaquely(relays, evt)

  await addEventToCache(evt, isReplaceableEvent(evt.kind) ? evt.created_at : undefined)
  emitNewEvent(evt)

  return evt
}

export async function* publishEvent(
  relayUrls: string[],
  event: NostrEvent
): AsyncGenerator<PublishResult> {
  const uniqueRelayUrls = Array.from(new Set(relayUrls.map(normalizeUrl)))
  const results: PublishResult[] = []
  let wake: (() => void) | undefined
  let finishedCount = 0

  const enqueue = (result: PublishResult) => {
    results.push(result)
    wake?.()
    wake = undefined
  }

  const workers = uniqueRelayUrls.map((url) =>
    (async () => {
      try {
        const relay = await pool.ensureRelay(url)
        relay.publishTimeout = 10_000 // 10s
        try {
          await relay.publish(event)
        } catch (error) {
          if (!(error instanceof Error && error.message.startsWith('auth-required'))) throw error
          const signer = await jotaiStore.get(signerAtom)
          if (!signer) throw error
          await authRelay(relay, signer)
          await relay.publish(event)
        }
        trackEventSeenOn(event.id, relay)
        enqueue({ relayUrl: url, success: true, reason: 'published' })
      } catch (error) {
        enqueue({ relayUrl: url, success: false, reason: getErrorMessage(error) })
      } finally {
        finishedCount++
        wake?.()
        wake = undefined
      }
    })()
  )

  while (finishedCount < uniqueRelayUrls.length || results.length) {
    if (results.length) {
      yield results.shift()!
    } else {
      await new Promise<void>((resolve) => {
        wake = resolve
      })
    }
  }

  await Promise.all(workers)
}

export async function publishEventOpaquely(relayUrls: string[], event: NostrEvent): Promise<void> {
  let success = false
  for await (const result of publishEvent(relayUrls, event)) {
    if (result.success) success = true
  }
  if (!success) throw new Error('failed to publish to all relays')
}

export async function publishEventAndDisplay(relayUrls: string[], event: NostrEvent) {
  const outcomes: PublishResult[] = []
  for await (const result of publishEvent(relayUrls, event)) {
    outcomes.push(result)
    displayPublishResult(result)
    if (result.success) {
      // as soon as it's out somewhere it's no longer pending, even if other relays are still slow
      unmarkPendingEvent(event.id)
      persistSeenOn(event).catch((err) => console.warn('failed to save seen-on relays:', err))
    }
  }

  if (!outcomes.some((result) => result.success)) {
    throw new AggregateError(
      outcomes.map((result) => new Error(`${result.relayUrl}: ${result.reason}`)),
      outcomes.map((result) => `${result.relayUrl}: ${result.reason}`).join('\n')
    )
  }

  return outcomes
}

export function emitNewEvent(event: NostrEvent) {
  for (const listener of newEventListeners) {
    listener(event)
  }
}

export function emitEphemeralEvent(event: NostrEvent) {
  for (const listener of newEventListeners) {
    listener(event)
  }
}

export function emitEventRemoved(eventId: string) {
  for (const listener of eventRemovedListeners) {
    listener(eventId)
  }
}

export async function signHttpAuth(url: string, method: string, description = '') {
  const signer = await jotaiStore.get(signerAtom)
  if (!signer) {
    throw new Error('Please login first to sign the event')
  }

  const event = await signer.signEvent({
    content: description,
    kind: kinds.HTTPAuth,
    created_at: now(),
    tags: [
      ['u', url],
      ['method', method]
    ]
  })

  return 'Nostr ' + btoa(JSON.stringify(event))
}

export function subscribeTimeline(
  subRequests: TFeedSubRequest[],
  filterModification: Filter,
  {
    onEvents,
    onNew,
    onClose
  }: {
    onEvents: (events: NostrEvent[], isFinal: boolean) => void
    onNew: (evt: NostrEvent) => void
    onClose?: (url: string, reason: string) => void
  }
): SubCloser {
  let subc: SubCloser
  const abort = new AbortController()

  const localFilters = subRequests
    .filter((req): req is Extract<TFeedSubRequest, { source: 'local' }> => req.source === 'local')
    .map(({ filter }) => ({
      ...filter,
      ...filterModification
    }))

  const relayRequests = subRequests
    .filter((req): req is Extract<TFeedSubRequest, { source: 'relays' }> => req.source === 'relays')
    .map(({ urls, filter }) => ({
      urls,
      filter: {
        ...filter,
        ...filterModification
      }
    }))

  // do local db requests
  let localSyncCallback: () => void
  let localNewCallback: (_: NostrEvent) => void
  let unsubNewEvent: (() => void) | undefined
  const local: Promise<[NostrEvent[], NostrEvent | undefined, string[]]> =
    localFilters.length === 0
      ? Promise.resolve([[], undefined, []])
      : (async () => {
          let newestEvent: NostrEvent | undefined

          // query from local db
          let events: NostrEvent[] = []
          for (let i = 0; i < localFilters.length; i++) {
            const queryEvents = await store.queryEvents(localFilters[i], 5_000)
            const first = queryEvents[0]

            if (first) {
              if (!newestEvent || newestEvent.created_at < first.created_at) {
                newestEvent = first
              }
            }

            if (events.length === 0) events = queryEvents
            else events = mergeReverseSortedLists(events, queryEvents)
          }

          // a background sync may be happening and we may be interested in it, handle when it ends
          localSyncCallback = debounce(
            async () => {
              for (let i = 0; i < localFilters.length; i++) {
                const filter = localFilters[i]
                for (const event of await store.queryEvents(filter, 5_000)) {
                  // check if this isn't already in the sorted array of events
                  const [_, exists] = binarySearch(events, (b) => {
                    if (event.id === b.id) return 0
                    if (event.created_at === b.created_at) return -1
                    return b.created_at - event.created_at
                  })
                  if (!exists) {
                    onNew(event)
                  }
                }
              }
            },
            2000,
            2800
          )
          current.onsync.push(localSyncCallback)

          // we'll use this for the live query
          const allAuthors = (
            await Promise.all(
              localFilters.map(async (f) => {
                if (f.authors) return f.authors
                return []
              })
            )
          ).flat()

          // listen for live updates to local db
          ready().then(() => {
            // group authors by their kinds set so each author is only subscribed
            // live to the kinds it actually needs (e.g. hardcoded feeds with a
            // restricted kinds list shouldn't trigger a live subscription for
            // unrelated kinds).
            const groups = new Map<string, { kinds: number[]; authors: string[] }>()
            for (const f of localFilters) {
              if (!f.authors?.length) continue
              const kinds = f.kinds ?? feedKinds
              const key = kinds
                .slice()
                .sort((a, b) => a - b)
                .join(',')
              let group = groups.get(key)
              if (!group) {
                group = { kinds, authors: [] }
                groups.set(key, group)
              }
              for (const pk of f.authors) {
                if (!group.authors.includes(pk)) group.authors.push(pk)
              }
            }
            for (const { kinds, authors } of groups.values()) {
              outbox.live(authors, kinds, {
                signal: abort.signal
              })
            }

            localNewCallback = (event: NostrEvent) => {
              if (matchFilters(localFilters, event)) onNew(event)
            }
            current.onnew.push(localNewCallback)

            unsubNewEvent = onNewEvent((event) => {
              if (matchFilters(localFilters, event)) onNew(event)
            })
          })

          return [events, newestEvent, allAuthors]
        })()

  // do relay requests
  const network =
    relayRequests.length === 0
      ? Promise.resolve([])
      : new Promise<NostrEvent[]>((resolve) => {
          let eosed = false

          let events: NostrEvent[] = []
          subc = pool.subscribeMap(
            relayRequests.flatMap(({ urls, filter }) => urls.map((url) => ({ url, filter }))),
            {
              label: 'f-timeline',
              onevent: (evt) => {
                if (!eosed) {
                  events.push(evt)
                } else {
                  onNew(evt)
                }

                if (localFilters.length) {
                  // if we're querying local filters simultaneously with external relays
                  // that means we want to store the results from the external relays in the local filters
                  // for consistency
                  addEventToCache(evt)
                }
              },
              oneose() {
                eosed = true
                resolve(events)
                events = []
              },
              onauth: async (authEvt: EventTemplate) => {
                // already logged in
                const signer = await jotaiStore.get(signerAtom)
                if (signer) {
                  return await signAuthEvent(authEvt, signer)
                }

                throw new Error(
                  "<not logged in, can't auth to relay during this.subscribeTimeline>"
                )
              },
              onclose(reasons) {
                if (onClose) {
                  for (const { url, reason } of reasons) {
                    onClose(url, reason)
                  }
                }
                resolve(events)
              }
            }
          )
        })

  if (localFilters.length > 0 && relayRequests.length > 0) {
    // if both exist, assume localFilters will load much faster and handle they first
    local.then(([eventsL]) => {
      if (localFilters.length > 1) eventsL.sort((a, b) => b.created_at - a.created_at)
      onEvents(eventsL, false) // not final: will be called again with all the events later
    })
  }

  let preliminarySub: SubCloser | undefined
  if (
    relayRequests.length === 0 &&
    localFilters.length === 1 &&
    (localFilters[0]!.authors?.length || 0) >= 1
  ) {
    // edge case: used mainly on the first time the app is used
    // in case we're fetching a _following feed_ or _profile feed_ solely from the local db but we have nothing
    // (or only very old events) do a temporary fallback relay query here while the sync completes
    local.then(async ([_, newestEvent, allAuthors]) => {
      if (!newestEvent || newestEvent.created_at < Date.now() / 1000 - 60 * 60 * 24 * 7) {
        const events: NostrEvent[] = []
        preliminarySub = pool.subscribeMap(
          await outboxFilterRelayBatch(allAuthors, [{ ...localFilters[0], limit: 10 }], {
            fallbackRelays: window.hallway.universe.bigRelayUrls
          }),
          {
            label: `f-temporary`,
            onevent(event) {
              events.push(event)
            },
            oneose() {
              preliminarySub!.close('preliminary req closed automatically on eose')
              events.sort((a, b) => b.created_at - a.created_at)
              onEvents(events, false) // not final. it will be final when the sync completes
            }
          }
        )

        // now the sync is complete, do the query again
        ready().then(async () => {
          const events: NostrEvent[] = new Array(200)
          let f = 0
          for (let i = 0; i < localFilters.length; i++) {
            for (const event of await store.queryEvents(localFilters[i], 5_000)) {
              events[f] = event
              f++
            }
          }
          events.length = f
          events.sort((a, b) => b.created_at - a.created_at)
          onEvents(events, true)
        })
      }
    })
  }

  // handle local results immediately
  local.then(([eventsL]) => {
    if (localFilters.length > 1) eventsL.sort((a, b) => b.created_at - a.created_at)
    onEvents(eventsL, false) // not final: will be called again with merged results later

    // handle network results and merge
    network.then((eventsN) => {
      if (eventsL.length > 0 && eventsN.length > 0) {
        // we have results both from relays and from local
        eventsN.sort((a, b) => b.created_at - a.created_at)
        const merged = mergeReverseSortedLists(eventsL, eventsN)
        onEvents(merged, true)
      } else if (eventsN.length) {
        // we only have results from relays
        if (relayRequests.length > 1 || relayRequests[0].urls.length > 1) {
          // sort results from relays -- but don't sort if we're querying a single relay,
          // as it will already send things sorted
          // (or in its own custom order that we'll respect because why not)
          eventsN.sort((a, b) => b.created_at - a.created_at)
        }
        onEvents(eventsN, true)
      } else if (eventsL.length) {
        // only local results, no results from relays, just call again as before, but signal completion
        onEvents(eventsL, true)
      } else {
        // no events found, but still need to signal completion
        onEvents([], true)
      }
    })
  })

  return {
    close() {
      onClose = undefined
      abort.abort('<subc>')
      subc?.close?.()
      preliminarySub?.close?.()
      unsubNewEvent?.()
      if (localSyncCallback) {
        const idx = current.onsync.indexOf(localSyncCallback)
        if (idx !== -1) {
          current.onsync.splice(idx, 1)
        }
      }
      if (localNewCallback) {
        const idx = current.onnew.indexOf(localNewCallback)
        if (idx !== -1) {
          current.onnew.splice(idx, 1)
        }
      }
    }
  }
}

export async function loadMoreTimeline(
  subRequests: TFeedSubRequest[],
  filterModification: Filter
): Promise<NostrEvent[]> {
  const abort = new AbortController()

  const localFilters = subRequests
    .filter((req): req is Extract<TFeedSubRequest, { source: 'local' }> => req.source === 'local')
    .map(({ filter }) => ({
      ...filter,
      ...filterModification
    }))

  const relayRequests = subRequests
    .filter((req): req is Extract<TFeedSubRequest, { source: 'relays' }> => req.source === 'relays')
    .map(({ urls, filter }) => ({
      urls,
      filter: {
        ...filter,
        ...filterModification
      }
    }))

  // do relay requests
  const network =
    relayRequests.length === 0
      ? Promise.resolve([])
      : new Promise<NostrEvent[]>((resolve) => {
          const events: NostrEvent[] = []
          const subc = pool.subscribeMap(
            relayRequests.flatMap(({ urls, filter }) => urls.flatMap((url) => ({ url, filter }))),
            {
              label: 'f-more',
              onevent(evt) {
                events.push(evt)
              },
              oneose() {
                subc.close()
                resolve(events)
              },
              onclose() {
                resolve(events)
              },

              onauth: async (authEvt: EventTemplate) => {
                // already logged in
                const signer = await jotaiStore.get(signerAtom)
                if (signer) {
                  return await signAuthEvent(authEvt, signer)
                }

                throw new Error("<not logged in, can't auth to relay during this.loadMoreTimeline>")
              }
            }
          )
        })

  // do local requests
  const local =
    localFilters.length === 0
      ? Promise.resolve([])
      : (async () => {
          // if any local filter targets specific authors, paginate the outbox
          // backwards down to `until` first so the local store actually contains
          // the older events we're about to query (otherwise pagination would
          // just return whatever is already cached)
          const until = filterModification.until
          if (until) {
            const authors = localFilters.flatMap((f) => f.authors ?? [])
            if (authors.length) {
              try {
                await outbox.before(authors, feedKinds, until, {
                  signal: abort.signal
                })
              } catch (err) {
                console.warn('outbox.before failed during loadMoreTimeline', err)
              }
            }
          }

          const events: NostrEvent[] = new Array(200)
          let f = 0
          for (let i = 0; i < localFilters.length; i++) {
            const filter = localFilters[i]
            for (const event of await store.queryEvents(filter, 5_000)) {
              events[f] = event
              f++
            }
          }
          events.length = f
          return events
        })()

  return Promise.all([local, network]).then(([eventsL, eventsN]) => {
    if (eventsL.length > 0 && eventsN.length > 0) {
      eventsL.push(...eventsN)
      eventsL.sort((a, b) => b.created_at - a.created_at)
      return eventsL
    } else if (eventsL.length) {
      if (localFilters.length > 1) eventsL.sort((a, b) => b.created_at - a.created_at)
      return eventsL
    } else if (eventsN.length) {
      if (relayRequests.length > 1 || relayRequests[0]?.urls.length > 1)
        eventsN.sort((a, b) => b.created_at - a.created_at)
      return eventsN
    } else {
      return []
    }
  })
}

export async function attemptDelete(targetEvent: NostrEvent): Promise<void> {
  const signer = await jotaiStore.get(signerAtom)
  if (!signer) throw new Error(t('You need to login first!'))

  if (jotaiStore.get(currentPubkeyAtom) !== targetEvent.pubkey)
    throw new Error(t('You can only delete your own events!'))

  const evtt = {
    kind: kinds.EventDeletion,
    content: '',
    tags: [
      ['k', `${targetEvent.kind}`],
      ['e', targetEvent.id]
    ],
    created_at: now()
  }

  if (isReplaceableEvent(targetEvent.kind))
    evtt.tags.push(['a', getReplaceableCoordinateFromEvent(targetEvent)])

  const deletionRequest = await signer.signEvent(evtt)
  const seenOnUrls = getSeenEventRelayUrls(targetEvent.id, targetEvent)
  const relays = await determineTargetRelays(targetEvent, {
    specifiedRelayUrls: targetEvent.tags.some(([t]) => t === '-') ? seenOnUrls : undefined,
    additionalRelayUrls: seenOnUrls
  })

  try {
    await publishEventAndDisplay(relays, deletionRequest)
  } catch (error) {
    console.error('Failed to send deletion request:', getErrorMessage(error))
  }

  await removeEventFromCache(targetEvent.id)
  await addEventToCache(deletionRequest)
  emitNewEvent(deletionRequest)
  emitEventRemoved(targetEvent.id)
}

export async function attemptDeleteFromRelay(
  targetEvent: NostrEvent,
  relayUrl: string
): Promise<void> {
  const signer = await jotaiStore.get(signerAtom)
  if (!signer) throw new Error(t('You need to login first!'))
  if (jotaiStore.get(currentPubkeyAtom) !== targetEvent.pubkey)
    throw new Error(t('You can only delete your own events!'))

  const deletionRequest = await signer.signEvent({
    kind: kinds.EventDeletion,
    content: '',
    tags: [
      ['k', `${targetEvent.kind}`],
      ['e', targetEvent.id],
      ...(isReplaceableEvent(targetEvent.kind)
        ? [['a', getReplaceableCoordinateFromEvent(targetEvent)]]
        : [])
    ],
    created_at: now()
  })

  await publishEventAndDisplay([relayUrl], deletionRequest)

  const normalizedRelayUrl = normalizeUrl(relayUrl)
  const remainingRelays = getSeenEventRelayUrls(targetEvent.id, targetEvent).filter(
    (url) => normalizeUrl(url) !== normalizedRelayUrl
  )
  const eventSeenOn = seenOn(targetEvent)
  eventSeenOn.splice(0, eventSeenOn.length, ...remainingRelays)
  const seenRelays = pool.seenOn.get(targetEvent.id)
  if (seenRelays) {
    for (const relay of seenRelays) {
      if (normalizeUrl(relay.url) === normalizedRelayUrl) seenRelays.delete(relay)
    }
  }
  await persistSeenOn(targetEvent)
}

export async function markEventSeenOn(event: NostrEvent, relayUrl: string): Promise<void> {
  const relayUrls = getSeenEventRelayUrls(event.id, event)
  if (relayUrls.some((url) => normalizeUrl(url) === normalizeUrl(relayUrl))) return

  seenOn(event).push(relayUrl)
  await persistSeenOn(event, [relayUrl])
}

// the store ignores saves of events it already has, so the only way to update the
// "seen_on" of an event is to delete it and save it again. writes are chained per event
// so they don't interleave, and each one reads the relay list at the time it runs.
const seenOnWrites = new Map<string, Promise<void>>()
export function persistSeenOn(event: NostrEvent, extraRelayUrls: string[] = []): Promise<void> {
  const next = (seenOnWrites.get(event.id) ?? Promise.resolve())
    .catch(() => {})
    .then(async () => {
      const relayUrls = Array.from(
        new Set([...getSeenEventRelayUrls(event.id, event), ...extraRelayUrls])
      )
      await store.deleteEvents([event.id])
      await store.saveEvent(event, {
        seenOn: relayUrls,
        lastAttempt: isReplaceableEvent(event.kind) ? event.created_at : undefined
      })
    })
  seenOnWrites.set(event.id, next)
  next
    .finally(() => {
      if (seenOnWrites.get(event.id) === next) seenOnWrites.delete(event.id)
    })
    .catch(() => {})
  return next
}

export function getSeenEventRelays(eventId: string) {
  return Array.from(pool.seenOn.get(eventId)?.values() || [])
}

export function getSeenEventRelayUrls(eventId: string, event?: NostrEvent) {
  const poolUrls = getSeenEventRelays(eventId).map((relay) => relay.url)

  // events loaded from the store may have a special list of "seenOn" relays attached
  const relays = event ? seenOn(event) : []
  const combined = new Set([...poolUrls, ...relays])

  return Array.from(combined)
}

export function getEventHints(eventId: string, event?: NostrEvent): string[] {
  return getSeenEventRelayUrls(eventId, event).filter((url) => !isLocalNetworkUrl(url))
}

export function getEventHint(eventId: string, event?: NostrEvent): string {
  return getSeenEventRelayUrls(eventId, event).find((url) => !isLocalNetworkUrl(url)) ?? ''
}

export function trackEventSeenOn(eventId: string, relay: any) {
  const seenOnMap = pool.seenOn as Map<string, Set<any>>
  let set = seenOnMap.get(eventId)
  if (!set) {
    set = new Set()
    seenOnMap.set(eventId, set)
  }
  set.add(relay)
}

export async function fetchEvents(
  urls: string[],
  filter: Filter,
  {
    cache = false,
    maxWait = 10_000
  }: {
    onevent?: (evt: NostrEvent) => void
    cache?: boolean
    maxWait?: number
  } = {}
) {
  const relays = urls.filter((url, i) => i === 0 || urls[i - 1] !== url)
  const events: NostrEvent[] = []

  await new Promise<void>((resolve) => {
    pool.subscribeEose(relays, filter, {
      label: 'f-fetch-events',
      maxWait,

      onauth: async (authEvt: EventTemplate) => {
        const signer = await jotaiStore.get(signerAtom)
        if (signer) {
          return await signAuthEvent(authEvt, signer)
        }

        throw new Error("<not logged in, can't auth to relay during this.fetchEvents>")
      },
      onevent: (event: NostrEvent) => {
        events.push(event)
        if (cache) {
          addEventToCache(event)
        }
      },
      onclose(_: { url: string; reason: string }[]) {
        resolve()
      }
    })
  })

  return events
}

export async function addEventToCache(event: NostrEvent, replaceableLastAttempt?: number) {
  const result = await store.saveEvent(event, {
    seenOn: Array.from(pool.seenOn.get(event.id) || []).map((relay) => relay.url),
    lastAttempt: replaceableLastAttempt
  })
  if (event.kind === 0) {
    addUsernameToIndex(nostrUserFromEvent(event))
  }
  return result
}

export async function removeEventFromCache(eventId: string) {
  return store.deleteEvents([eventId])
}

export async function fetchEvent(
  idOrCode: string | EventPointer | AddressPointer
): Promise<NostrEvent | undefined> {
  let filter: Filter | undefined
  let relayHints: string[] = []
  let authorHint: string | undefined
  if (typeof idOrCode === 'string') {
    if (isHex32(idOrCode)) {
      filter = { ids: [idOrCode] }
    } else {
      const { type, data } = decode(idOrCode)
      switch (type) {
        case 'note':
          filter = { ids: [data] }
          break
        case 'nevent':
          filter = { ids: [data.id] }
          if (data.relays) relayHints = data.relays
          if (data.author) authorHint = data.author
          break
        case 'naddr':
          filter = {
            authors: [data.pubkey],
            kinds: [data.kind],
            limit: 1
          }
          authorHint = data.pubkey
          if (kinds.isAddressableKind(data.kind)) {
            filter['#d'] = [data.identifier]
          }
          if (data.relays) relayHints = data.relays
      }
    }
  } else if ('id' in idOrCode) {
    // EventPointer
    filter = { ids: [idOrCode.id] }
    if (idOrCode.relays) relayHints = idOrCode.relays
    if (idOrCode.author) authorHint = idOrCode.author
  } else {
    // AddressPointer
    filter = {
      authors: [idOrCode.pubkey],
      kinds: [idOrCode.kind],
      limit: 1
    }
    authorHint = idOrCode.pubkey
    if (kinds.isAddressableKind(idOrCode.kind)) {
      filter['#d'] = [idOrCode.identifier]
    }
    if (idOrCode.relays) relayHints = idOrCode.relays
  }

  if (!filter) {
    throw new Error(`can't fetch ${typeof idOrCode === 'string' ? idOrCode : 'pointer'}`)
  }

  const now_ = now()
  const isReplaceable =
    filter.kinds?.length === 1 &&
    filter.authors?.length === 1 &&
    (kinds.isReplaceableKind(filter.kinds[0]) ||
      (kinds.isAddressableKind(filter.kinds[0]) && filter['#d']?.length === 1))

  let storedEvent: NostrEvent | undefined
  if (isReplaceable) {
    const dtag = filter['#d']?.[0]
    const [[lastAttempt, event]] = (await store.loadReplaceables([
      [filter.kinds![0], filter.authors![0], dtag]
    ])) as [number | undefined, NostrEvent][]

    const TWO_DAYS = 2 * 24 * 60 * 60
    const isEmpty = event?.id === '0'.repeat(64)

    if (event) {
      if (isEmpty) {
        if (lastAttempt !== undefined && lastAttempt > now_ - TWO_DAYS) {
          return undefined // recent attempt, nothing found, skip
        }
      } else if (lastAttempt !== undefined && lastAttempt > now_ - TWO_DAYS) {
        return event // fresh enough
      }
    }

    storedEvent = !isEmpty && event ? event : undefined
  } else if (filter.ids?.length === 1) {
    for (const event of await store.queryEvents(filter, 1)) {
      return event
    }
  } else {
    throw new Error(`can only fetch replaceables or events by id, got ${JSON.stringify(filter)}`)
  }

  // couldn't find anything in local database, try relays now
  //

  // start fetching this here so it's finished later when we need it
  const authorRelays = authorHint && loadRelayList(authorHint)

  // try the relay hints first
  if (relayHints.length) {
    relayHints = relayHints.map(normalizeUrl)
    const event = await pool.get(relayHints, filter, { label: 'f-specific-event-1' })
    if (event) {
      addEventToCache(event, isReplaceable ? now_ : undefined)
      return event
    }
  }

  // at this point we may already have our author hints so let's try those
  let authorRelaysUrls: string[] = []
  if (authorRelays) {
    authorRelaysUrls = (await authorRelays).items
      .filter((r) => r.write && !relayHints.includes(r.url))
      .map((r) => r.url)
    if (authorRelaysUrls.length) {
      const event = await pool.get(authorRelaysUrls, filter, { label: 'f-specific-event-2' })
      if (event) {
        addEventToCache(event, isReplaceable ? now_ : undefined)
        return event
      }
    }
  }

  // if we got nothing or there were no hints, try the big relays (except the ones we've already tried)
  const bigRelayHints = window.hallway.universe.bigRelayUrls.filter(
    (br: string) => !(relayHints.includes(br) || authorRelaysUrls.includes(br))
  )
  bigRelayHints.push('wss://cache2.primal.net/v1')
  if (bigRelayHints.length) {
    const event = await pool.get(bigRelayHints, filter, { label: 'f-specific-event-3' })
    if (event) {
      addEventToCache(event, isReplaceable ? now_ : undefined)
      return event
    }
  }

  if (isReplaceable) {
    if (storedEvent) {
      // have old cached event, return it as fallback
      return storedEvent
    }

    // save our failed attempt
    const dtag = filter['#d']?.[0]
    store.saveEvent(
      {
        id: '0'.repeat(64),
        pubkey: filter.authors![0],
        kind: filter.kinds![0],
        sig: '0'.repeat(128),
        tags: dtag !== undefined ? ([['d', dtag]] as string[][]) : [],
        created_at: 0,
        content: ''
      } as NostrEvent,
      { lastAttempt: now_ }
    )
  }

  return undefined
}

export async function searchProfiles(relayUrls: string[], filter: Filter): Promise<NostrUser[]> {
  const events = await pool.querySync(
    relayUrls,
    {
      ...filter,
      kinds: [kinds.Metadata]
    },
    { label: 'f-search-profiles', maxWait: 5_000 }
  )

  const profiles = events.map(nostrUserFromEvent)
  await Promise.allSettled(profiles)
  return profiles
}

export function searchPubKeysFromLocal(query: string, limit: number = 100): string[] {
  return userIndex.search(query, { limit }) as string[]
}

export function addUsernameToIndex(profile: NostrUser) {
  if (userIndex.contain(profile.pubkey)) return

  const text = [
    profile.metadata.display_name?.trim() ?? '',
    profile.metadata.name?.trim() ?? '',
    profile.metadata.nip05
      ?.split?.('@')
      ?.map?.((s: string) => s.trim())
      ?.join?.(' ') ?? '',
    profile.metadata.about ?? ''
  ]
    .join(' ')
    .trim()
  if (!text) return

  userIndex.add(profile.pubkey, text)
}

export async function fetchMuteList(
  pubkey: string,
  forceUpdate: boolean | NostrEvent = false
): Promise<{ list: TMutedList; event: NostrEvent | null }> {
  const muteList: TMutedList = {
    public: [],
    private: []
  }

  const result = await loadMuteList(pubkey, [], forceUpdate)
  console.debug('[mute] loadMuteList result:', {
    eventId: result.event?.id,
    createdAt: result.event?.created_at,
    itemsCount: result.items.length
  })

  muteList.public = result.items.filter((item) => item.label === 'pubkey').map((item) => item.value)

  if (result.event?.content.length && result.event.pubkey === jotaiStore.get(currentPubkeyAtom)) {
    const signer = await jotaiStore.get(signerAtom)
    if (signer?.nip04Decrypt) {
      try {
        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('nip04Decrypt timeout')), 5000)
        )
        const plaintext = await Promise.race([
          result.event.content.includes('?iv=')
            ? signer.nip04Decrypt(pubkey, result.event.content)
            : signer.nip44Decrypt?.(pubkey, result.event.content),
          timeout
        ])

        if (plaintext) {
          let privateTags: string[][] = []
          try {
            privateTags = JSON.parse(plaintext)
            if (!Array.isArray(privateTags) || !privateTags.every(Array.isArray)) {
              throw new Error()
            }
          } catch {
            privateTags = []
          }

          for (let i = 0; i < privateTags.length; i++) {
            const tag = privateTags[i]
            if (tag[0] === 'p' && tag.length >= 2 && isHex32(tag[1])) {
              muteList.private.push(tag[1])
            }
          }
        }
      } catch (e) {
        console.error('[mute] nip04Decrypt failed:', e)
      }
    }
  }

  console.debug('[mute] fetchMuteList result:', {
    public: muteList.public.length,
    private: muteList.private.length
  })
  return { list: muteList, event: result.event ?? null }
}

//
// database cleanup
//
// deletes events older than 4 months (1 month for kinds outside feedKinds),
// runs weekly, but never during the first 15 minutes of a session.
// events that would land in the next deletion batch when stored get their
// deletion postponed by 4 batches (tracked by 8-char id prefix on localStorage);
// if more than MAX_SCHEDULES postponements pile up, the FORCE_DELETE_COUNT
// soonest ones are deleted immediately.
//

const LAST_CLEANUP_KEY = 'hallway:last-cleanup'
const DELETION_SCHEDULES_KEY = 'hallway:deletion-schedules'
const WEEK = 7 * 24 * 60 * 60
const MONTH = 30 * 24 * 60 * 60
const FOUR_MONTHS = 4 * MONTH
const POSTPONE_BATCHES = 4
const MAX_SCHEDULES = 500
const FORCE_DELETE_COUNT = 100
const SESSION_GRACE_MS = 15 * 60 * 1000
const CLEANUP_MAX_ROUNDS = 50
const DELETE_CHUNK_SIZE = 500
const QUERY_BATCH_SIZE = 5000

const feedKindSet = new Set(feedKinds)
let deletionSchedules: Record<string, number> | undefined
let cleanupTimer: ReturnType<typeof setTimeout> | undefined

function loadDeletionSchedules(): Record<string, number> {
  if (deletionSchedules) return deletionSchedules
  try {
    deletionSchedules = JSON.parse(localStorage.getItem(DELETION_SCHEDULES_KEY) || '{}')
  } catch {
    deletionSchedules = {}
  }
  return deletionSchedules!
}

function persistDeletionSchedules() {
  try {
    localStorage.setItem(DELETION_SCHEDULES_KEY, JSON.stringify(deletionSchedules ?? {}))
  } catch (err) {
    console.warn('failed to persist deletion schedules', err)
  }
}

function trackRecentlyStoredOldEvent(event: NostrEvent) {
  try {
    if (!event?.id || event.id === '0'.repeat(64)) return
    const cutoff = now() - (feedKindSet.has(event.kind) ? FOUR_MONTHS : MONTH)
    if (event.created_at >= cutoff) return // not part of an upcoming deletion batch
    const schedules = loadDeletionSchedules()
    schedules[event.id.slice(0, 8)] = now() + POSTPONE_BATCHES * WEEK
    persistDeletionSchedules()
  } catch (err) {
    console.warn('failed to track deletion postponement', err)
  }
}

function setupEventDeletionTracking() {
  const proto = RedEventStore.prototype as any
  if (proto.__cleanupHooked) return
  proto.__cleanupHooked = true
  const originalSaveEvent = proto.saveEvent
  proto.saveEvent = function (event: NostrEvent, ...args: any[]) {
    trackRecentlyStoredOldEvent(event)
    return originalSaveEvent.call(this, event, ...args)
  }
}

function scheduleNextCleanup() {
  if (cleanupTimer) clearTimeout(cleanupTimer)

  // never run during the first 15 minutes of the app being open
  let delay = SESSION_GRACE_MS
  try {
    const last = parseInt(localStorage.getItem(LAST_CLEANUP_KEY) || '') || 0
    if (last > 0) delay = Math.max(delay, last * 1000 + WEEK * 1000 - Date.now())
  } catch (err) {
    console.warn('failed to read last cleanup time', err)
  }

  cleanupTimer = setTimeout(performCleanup, delay)
}

async function performCleanup() {
  try {
    try {
      localStorage.setItem(LAST_CLEANUP_KEY, `${now()}`)
    } catch (err) {
      console.warn('failed to save last cleanup time', err)
    }

    const nowTs = now()
    const schedules = loadDeletionSchedules()

    // postponements that have already expired are no longer honored
    const active = Object.entries(schedules)
      .filter(([, ts]) => ts > nowTs)
      .sort((a, b) => a[1] - b[1])

    // too many postponements: force-delete the soonest ones right away
    const forced = new Set<string>()
    if (active.length > MAX_SCHEDULES) {
      for (const [prefix] of active.splice(0, FORCE_DELETE_COUNT)) forced.add(prefix)
    }

    deletionSchedules = Object.fromEntries(active)
    persistDeletionSchedules()

    const cutoffOther = nowTs - MONTH
    const cutoffFeed = nowTs - FOUR_MONTHS

    let deleted = 0
    let until = cutoffOther
    for (let round = 0; round < CLEANUP_MAX_ROUNDS; round++) {
      const oldEvents = await store.queryEvents({ until }, QUERY_BATCH_SIZE)
      if (oldEvents.length === 0) break

      const toDelete: string[] = []
      for (const evt of oldEvents) {
        if (forced.has(evt.id.slice(0, 8))) {
          toDelete.push(evt.id)
        } else if (deletionSchedules![evt.id.slice(0, 8)]) {
          continue // postponed to the future
        } else if (!feedKindSet.has(evt.kind) || evt.created_at < cutoffFeed) {
          toDelete.push(evt.id)
        }
      }
      for (let i = 0; i < toDelete.length; i += DELETE_CHUNK_SIZE) {
        await store.deleteEvents(toDelete.slice(i, i + DELETE_CHUNK_SIZE))
      }
      deleted += toDelete.length

      if (oldEvents.length < QUERY_BATCH_SIZE) break

      // move past the events we decided to keep
      if (toDelete.length < oldEvents.length) {
        const oldest = oldEvents[oldEvents.length - 1].created_at
        until = oldest < until ? oldest : until - 1
      }
    }

    if (deleted > 0) console.debug(`:: cleanup deleted ${deleted} events`)
  } catch (err) {
    console.warn('cleanup failed', err)
  } finally {
    scheduleNextCleanup()
  }
}

void init()
